function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { CssTools } from './CssTools.js';
import { resolveCssColor } from './ColorTools.js';
/**
 * Builder to create the category-axis style
 */

export class CategoryAxisBuilder {
  constructor() {
    _defineProperty(this, "title", null);

    _defineProperty(this, "horizontal", null);

    _defineProperty(this, "fontFamily", null);

    _defineProperty(this, "_hasLabels", null);

    _defineProperty(this, "_hasIcons", null);

    _defineProperty(this, "justifyTickContent", 'SpaceGreedily');
  }

  /**
   * The labels to be used for the categories
   *
   * @param newLabels - the labels to be used for the categories
   */
  set labels(newLabels) {
    this._hasLabels = newLabels.length > 0 && newLabels.find(value => !!value) !== undefined;
  }

  /**
   * The icons to be used for the categories
   *
   * @param newIcons - the icons to be used for the categories
   */
  set icons(newIcons) {
    this._hasIcons = newIcons.length > 0 && newIcons.find(value => !!value) !== undefined;
  }
  /**
   * Determines how labels and icons associated with a tick are laid out.
   *
   * @default SpaceGreedily
   */


  /**
   * Sets the width of the vertical axis in rem.
   *
   * The width must be in [0..100] or undefined.
   * If `undefined` the width will be computed automatically.
   *
   * @param width - the width of the vertical axis in rem.
   */
  set axisWidth(width) {
    if (width < 0) {
      width = 0;
    }

    if (width > 100) {
      width = 100;
    }

    this._axisWidth = width;
  }
  /**
   * Builds a style object for category axes
   */


  build() {
    // check that everything is set correctly
    if (this.horizontal === null) {
      throw new Error('no orientation set');
    }

    if (this.fontFamily === null) {
      throw new Error('no font family set');
    }

    if (this._hasLabels === null) {
      throw new Error('no labels set');
    }

    if (this._hasIcons === null) {
      throw new Error('no icons set');
    }

    const style = {};
    style.tickFont = {
      family: this.fontFamily,
      size: CssTools.REM2PIXEL(CategoryAxisBuilder.TICK_FONT_SIZE),
      style: 'normal',
      weight: 400 // regular

    };
    style.titleFont = {
      family: this.fontFamily,
      size: CssTools.REM2PIXEL(CategoryAxisBuilder.TITLE_FONT_SIZE),
      style: 'normal',
      weight: 600 // semi-bold

    };
    style.axisSide = this.horizontal ? 'Bottom' : 'Left';
    style.justifyTickContent = this.justifyTickContent;
    style.tickLabelGap = CssTools.REM2PIXEL(CategoryAxisBuilder.AXIS_LINE_GAP);
    style.tickLabelColor = resolveCssColor('var(--davinci-color-background-elements-5)');
    style.tickLength = 0.0;
    style.axisLineColor = resolveCssColor('var(--davinci-color-background-elements-3)');
    style.axisLineWidth = CategoryAxisBuilder.AXIS_LINE_WIDTH;
    style.minTickLabelDistance = CssTools.REM2PIXEL(CategoryAxisBuilder.MIN_TICK_LABEL_DISTANCE);
    style.iconSize = CssTools.REM2PIXEL(CategoryAxisBuilder.ICON_SIZE);
    style.title = this.title;
    style.titleGap = this.horizontal ? CssTools.REM2PIXEL(CategoryAxisBuilder.HORIZONTAL_AXIS_TITLE_GAP) : CssTools.REM2PIXEL(CategoryAxisBuilder.VERTICAL_AXIS_TITLE_GAP);
    style.titleColor = resolveCssColor('var(--davinci-color-background-elements-4)');
    style.axisSize = this._computeAxisSize(CssTools.PIXELS2LINE_HEIGHT(style.titleFont.size), CssTools.PIXELS2LINE_HEIGHT(style.tickFont.size));
    return style;
  }
  /**
   * Computes the width/height of the vertical/horizontal category axis (in pixels).
   *
   * @param titleHeight - the height of the axis title in pixels
   * @param tickLabelHeight - the height of the tick labels in pixels
   */


  _computeAxisSize(titleHeight, tickLabelHeight) {
    return this.horizontal ? this._computeHorizontalAxisHeight(titleHeight, tickLabelHeight) : this._computeVerticalAxisWidth(tickLabelHeight);
  }
  /**
   * Computes the height of the horizontal category axis (in pixels).
   *
   * @param titleHeight - the height of the axis title in pixels
   * @param tickLabelHeight - the height of the tick labels in pixels
   */


  _computeHorizontalAxisHeight(titleHeight, tickLabelHeight) {
    var _this$title, _this$title$trim;

    let axisHeight = CategoryAxisBuilder.AXIS_LINE_WIDTH;

    if (this._hasLabels || this._hasIcons) {
      axisHeight += CssTools.REM2PIXEL(CategoryAxisBuilder.AXIS_LINE_GAP);
    }

    if (this._hasLabels && this._hasIcons) {
      axisHeight += CssTools.REM2PIXEL(CategoryAxisBuilder.LABEL_ICON_GAP);
    }

    if (this._hasLabels) {
      axisHeight += tickLabelHeight;
    }

    if (this._hasIcons) {
      axisHeight += CssTools.REM2PIXEL(CategoryAxisBuilder.ICON_SIZE);
    }

    if (((_this$title = this.title) === null || _this$title === void 0 ? void 0 : (_this$title$trim = _this$title.trim()) === null || _this$title$trim === void 0 ? void 0 : _this$title$trim.length) > 0) {
      if (this._hasLabels || this._hasIcons) {
        axisHeight += CssTools.REM2PIXEL(CategoryAxisBuilder.HORIZONTAL_AXIS_TITLE_GAP);
      }

      axisHeight += titleHeight;
    }

    if (axisHeight <= CategoryAxisBuilder.AXIS_LINE_WIDTH) {
      // Add half of the tick-font-size to the size in order to prevent the first tick of the value-axis from being clipped
      axisHeight += tickLabelHeight * 0.5;
    }

    return axisHeight;
  }
  /**
   * Computes the width of the vertical category axis (in pixels).
   *
   * @param tickLabelHeight -  the height of the tick labels in pixels
   */


  _computeVerticalAxisWidth(tickLabelHeight) {
    if (this._axisWidth !== undefined) {
      return CssTools.REM2PIXEL(this._axisWidth);
    }

    let axisWidth = CategoryAxisBuilder.AXIS_LINE_WIDTH;

    if (this._hasLabels || this._hasIcons) {
      axisWidth += CssTools.REM2PIXEL(CategoryAxisBuilder.AXIS_LINE_GAP);
    }

    if (this._hasLabels && this._hasIcons) {
      axisWidth += CssTools.REM2PIXEL(CategoryAxisBuilder.LABEL_ICON_GAP);
    }

    if (this._hasLabels) {
      axisWidth += CssTools.REM2PIXEL(CategoryAxisBuilder.LABEL_MAX_WIDTH);
    }

    if (this._hasIcons) {
      axisWidth += CssTools.REM2PIXEL(CategoryAxisBuilder.ICON_SIZE);
    }

    if (axisWidth <= CategoryAxisBuilder.AXIS_LINE_WIDTH) {
      // Add half of the tick-label height to the width in order to prevent the first tick of the value-axis from being clipped
      axisWidth += tickLabelHeight * 0.5;
    }

    return axisWidth;
  }

}

_defineProperty(CategoryAxisBuilder, "AXIS_LINE_WIDTH", 1.0);

_defineProperty(CategoryAxisBuilder, "AXIS_LINE_GAP", 0.5);

_defineProperty(CategoryAxisBuilder, "LABEL_ICON_GAP", 0.25);

_defineProperty(CategoryAxisBuilder, "ICON_SIZE", 2.0);

_defineProperty(CategoryAxisBuilder, "TITLE_FONT_SIZE", 0.75);

_defineProperty(CategoryAxisBuilder, "TICK_FONT_SIZE", 0.75);

_defineProperty(CategoryAxisBuilder, "LABEL_MAX_WIDTH", 4.0);

_defineProperty(CategoryAxisBuilder, "HORIZONTAL_AXIS_TITLE_GAP", 0.25);

_defineProperty(CategoryAxisBuilder, "VERTICAL_AXIS_TITLE_GAP", 0.25 + CategoryAxisBuilder.TICK_FONT_SIZE * 0.5);

_defineProperty(CategoryAxisBuilder, "MIN_TICK_LABEL_DISTANCE", 0.75);
